<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const FIELD_TYPES = [
  { label: t('site.f.dynamic_form.opt_single_line', 'Single line'), value: 'text' },
  { label: t('site.f.dynamic_form.opt_email', 'Email'), value: 'email' },
  { label: t('site.f.dynamic_form.opt_phone', 'Phone'), value: 'tel' },
  { label: t('site.f.dynamic_form.opt_paragraph', 'Paragraph'), value: 'textarea' },
  { label: t('site.f.dynamic_form.opt_dropdown', 'Dropdown'), value: 'select' },
  { label: t('site.f.dynamic_form.opt_checkbox', 'Checkbox'), value: 'checkbox' },
  { label: t('site.f.dynamic_form.opt_file_upload', 'File upload'), value: 'file' }
]

const addField = () => {
  props.block.data.fields = [
    ...(props.block.data.fields ?? []),
    { name: '', label: '', type: 'text' as const, required: false }
  ]
}
const removeField = (i: number) => {
  props.block.data.fields?.splice(i, 1)
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>
  <UFormField :label="t('site.f.common.intro_optional', 'Intro (optional)')">
    <UTextarea
      v-model="block.data.subtext"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.common.submit_url_label', 'Where submissions go')"
    :help="t('site.f.dynamic_form.submitted_answers_are_sent_to_this_url_a_help', 'Submitted answers (including any uploaded file) are sent to this URL as a normal form request and it must return a JSON response. Point this at a form on this platform, e.g. /api/public/forms/your-form-slug/submit. Must start with https://.')"
    :error="!block.data.submit_url ? t('site.f.dynamic_form.err_submit_url', 'This block needs a submit URL before the page can be saved.') : undefined"
  >
    <UInput
      v-model="block.data.submit_url"
      :placeholder="t('site.f.dynamic_form.https_your_tenant_example_com_api_public', 'https://your-tenant.example.com/api/public/forms/.../submit')"
      class="w-full"
    />
  </UFormField>

  <SiteEditorArrayItemCard
    v-for="(field, i) in block.data.fields ?? []"
    :key="i"
    :label="`${t('site.f.dynamic_form.item_label', 'Field')} ${i + 1}`"
    @remove="removeField(i)"
  >
    <UInput
      v-model="field.label"
      :placeholder="t('site.f.common.field_label_placeholder', 'Label shown to visitors')"
      class="w-full"
    />
    <UInput
      v-model="field.name"
      :placeholder="t('site.f.dynamic_form.field_key_sent_to_the_backend_e_g_email', 'Field key sent to the backend, e.g. email')"
      class="w-full"
    />
    <USelect
      v-model="field.type"
      :items="FIELD_TYPES"
      class="w-full"
    />
    <UInput
      v-if="!['checkbox', 'select', 'file'].includes(field.type)"
      :model-value="field.placeholder ?? ''"
      :placeholder="t('site.f.common.placeholder_field_label', 'Placeholder (optional)')"
      class="w-full"
      @update:model-value="field.placeholder = String($event)"
    />
    <UTextarea
      v-if="field.type === 'select'"
      :model-value="(field.options ?? []).join('\n')"
      :rows="3"
      :placeholder="t('site.f.common.options_per_line_placeholder', 'One option per line')"
      class="w-full"
      @update:model-value="field.options = String($event).split('\n').map(o => o.trim()).filter(Boolean)"
    />
    <div class="flex items-center justify-between gap-2 py-1">
      <span class="text-xs text-muted">{{ t('site.f.common.required_label', 'Required') }}</span>
      <USwitch v-model="field.required" />
    </div>
  </SiteEditorArrayItemCard>

  <UButton
    size="sm"
    variant="outline"
    color="neutral"
    icon="i-lucide-plus"
    @click="addField"
  >
    {{ t('site.f.dynamic_form.add_item', 'Add field') }}
  </UButton>

  <UFormField :label="t('site.f.common.button_text', 'Button text')">
    <UInput
      :model-value="block.data.button_text ?? ''"
      :placeholder="t('site.f.common.send_placeholder', 'Send')"
      class="w-full"
      @update:model-value="block.data.button_text = String($event)"
    />
  </UFormField>

  <SiteEditorFieldGroup :label="t('site.f.common.advanced_label', 'Advanced')">
    <UFormField
      :label="t('site.f.dynamic_form.button_text_while_sending', 'Button text while sending')"
      :help="t('site.f.dynamic_form.shown_on_the_button_during_submission_le_help', 'Shown on the button during submission. Leave blank for “Sending…”.')"
    >
      <UInput
        :model-value="block.data.submitting_text ?? ''"
        :placeholder="t('site.f.dynamic_form.sending', 'Sending…')"
        class="w-full"
        @update:model-value="block.data.submitting_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.dynamic_form.error_message', 'Error message')"
      :help="t('site.f.dynamic_form.shown_if_the_submission_fails_and_the_se_help', 'Shown if the submission fails and the server sends no message of its own.')"
    >
      <UInput
        :model-value="block.data.error_text ?? ''"
        :placeholder="t('site.f.dynamic_form.something_went_wrong_please_try_again', 'Something went wrong — please try again.')"
        class="w-full"
        @update:model-value="block.data.error_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.confirmation_message_label', 'Confirmation message')"
      :help="t('site.f.dynamic_form.shown_after_a_successful_submission_if_t_help', 'Shown after a successful submission if the backend doesn\'t send its own message.')"
    >
      <UInput
        :model-value="block.data.success_text ?? ''"
        :placeholder="t('site.f.common.thanks_placeholder', 'Thanks — we\'ll be in touch.')"
        class="w-full"
        @update:model-value="block.data.success_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.consent_label', 'Consent checkbox (optional)')"
      :help="t('site.f.common.consent_help', 'When set, the visitor must tick this before submitting.')"
    >
      <UTextarea
        :model-value="block.data.consent_text ?? ''"
        :rows="2"
        :placeholder="t('site.f.common.privacy_consent_placeholder', 'I accept the privacy policy.')"
        class="w-full"
        @update:model-value="block.data.consent_text = String($event)"
      />
    </UFormField>
  </SiteEditorFieldGroup>
</template>